import { Type } from '@angular/core';

export interface GameTemplateEntry {
  loader: () => Promise<Type<any>>;
  /** Extra static inputs beyond the common ones */
  inputs?: Record<string, any>;
  /** Extra inputs computed from the game data at runtime */
  inputsFn?: (game: any) => Record<string, any>;
  /**
   * How to route the component output:
   *   undefined    → subscribe 'answersSubmit' → call parent answersSubmit()
   *   'difficulty' → subscribe 'answersSubmit' → call parent answersDifficultySubmit()
   *   'play'       → subscribe 'playSubmit'    → call parent startGame()
   *   null         → no output subscription (flashcards, opera, 3d_vocabulary…)
   */
  submit?: 'difficulty' | 'play' | null;
  /** Common inputs to skip for this component (e.g. opera has no gameDesc/gameDescAudio) */
  omitInputs?: string[];
}

// Common inputs injected for every template (unless listed in omitInputs):
//   idSection, idTheme, idLevel, gameName, gameDesc, gameDescAudio, questions

export const GAME_TEMPLATE_REGISTRY: Record<string, GameTemplateEntry> = {

  // ── GameFindWordFromTranslationComponent ─────────────────────────────────
  find_word_from_translation: {
    loader: () => import('../../../components/games/templates/game-find-word-from-translation/game-find-word-from-translation.component').then(m => m.GameFindWordFromTranslationComponent),
  },
  find_word_from_translation_easy: {
    loader: () => import('../../../components/games/templates/game-find-word-from-translation/game-find-word-from-translation.component').then(m => m.GameFindWordFromTranslationComponent),
  },
  ja_find_word_from_kanji: {
    loader: () => import('../../../components/games/templates/game-find-word-from-translation/game-find-word-from-translation.component').then(m => m.GameFindWordFromTranslationComponent),
  },
  find_sentence_from_translation: {
    loader: () => import('../../../components/games/templates/game-find-word-from-translation/game-find-word-from-translation.component').then(m => m.GameFindWordFromTranslationComponent),
  },
  find_sentence_from_translation_easy: {
    loader: () => import('../../../components/games/templates/game-find-word-from-translation/game-find-word-from-translation.component').then(m => m.GameFindWordFromTranslationComponent),
  },

  // ── GameFindTranslationFromWordComponent ─────────────────────────────────
  find_word_from_gap_text: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: true },
  },
  find_word_from_gap_text_grammar: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: true },
  },
  toeic_first_reading: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: true },
  },
  find_nature_from_gap_text: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: false },
  },
  find_translation_from_word: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: false, gameTag: 'find_translation_from_word' },
  },
  find_translation_from_sentence: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: false, gameTag: 'find_translation_from_sentence' },
  },
  find_translation_from_sentence_hard: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: false, gameTag: 'find_translation_from_sentence_hard' },
  },
  ja_find_kanji_from_word: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-word/game-find-translation-from-word.component').then(m => m.GameFindTranslationFromWordComponent),
    inputs: { instantAnswer: false, gameTag: 'ja_find_kanji_from_word' },
  },

  // ── GameSecondReadingComponent ────────────────────────────────────────────
  toeic_second_reading: {
    loader: () => import('../../../components/games/templates/game-second-reading/game-second-reading.component').then(m => m.GameSecondReadingComponent),
    inputs: { displayText: false, displayAudio: false },
  },
  toeic_third_reading: {
    loader: () => import('../../../components/games/templates/game-second-reading/game-second-reading.component').then(m => m.GameSecondReadingComponent),
    inputs: { displayText: true, displayAudio: false },
  },
  toeic_fourth_listening: {
    loader: () => import('../../../components/games/templates/game-second-reading/game-second-reading.component').then(m => m.GameSecondReadingComponent),
    inputs: { displayText: false, displayAudio: true },
  },

  // ── GameFirstListeningComponent ───────────────────────────────────────────
  toeic_first_listening: {
    loader: () => import('../../../components/games/templates/game-first-listening/game-first-listening.component').then(m => m.GameFirstListeningComponent),
    inputs: { questionAudio: false },
  },
  toeic_second_listening: {
    loader: () => import('../../../components/games/templates/game-first-listening/game-first-listening.component').then(m => m.GameFirstListeningComponent),
    inputs: { questionAudio: true },
  },

  // ── GameThirdListeningComponent ───────────────────────────────────────────
  toeic_third_listening: {
    loader: () => import('../../../components/games/templates/game-third-listening/game-third-listening.component').then(m => m.GameThirdListeningComponent),
  },

  // ── GameWriteConjugationFromGapTextComponent ──────────────────────────────
  write_conjugation_from_gap_text: {
    loader: () => import('../../../components/games/templates/game-write-conjugation-from-gap-text/game-write-conjugation-from-gap-text.component').then(m => m.GameWriteConjugationFromGapTextComponent),
  },

  // ── GameFindSoundFromTranslationComponent ─────────────────────────────────
  find_sound_from_translation: {
    loader: () => import('../../../components/games/templates/game-find-sound-from-translation/game-find-sound-from-translation.component').then(m => m.GameFindSoundFromTranslationComponent),
  },

  // ── GameWordMistakeComponent ──────────────────────────────────────────────
  word_mistake: {
    loader: () => import('../../../components/games/templates/game-word-mistake/game-word-mistake.component').then(m => m.GameWordMistakeComponent),
  },

  // ── GameWordMistakeEasyComponent ──────────────────────────────────────────
  word_mistake_easy: {
    loader: () => import('../../../components/games/templates/game-word-mistake-easy/game-word-mistake-easy.component').then(m => m.GameWordMistakeEasyComponent),
  },

  // ── GameCrossWordsComponent ───────────────────────────────────────────────
  cross_words: {
    loader: () => import('../../../components/games/templates/game-cross-words/game-cross-words.component').then(m => m.GameCrossWordsComponent),
    submit: 'play',
  },

  // ── GameLotoComponent ─────────────────────────────────────────────────────
  loto: {
    loader: () => import('../../../components/games/templates/game-loto/game-loto.component').then(m => m.GameLotoComponent),
  },

  // ── GameFindImageFromSoundComponent ───────────────────────────────────────
  find_image_from_sound: {
    loader: () => import('../../../components/games/templates/game-find-image-from-sound/game-find-image-from-sound.component').then(m => m.GameFindImageFromSoundComponent),
  },

  // ── GameFindImageFromWordComponent ────────────────────────────────────────
  find_image_from_word: {
    loader: () => import('../../../components/games/templates/game-find-image-from-word/game-find-image-from-word.component').then(m => m.GameFindImageFromWordComponent),
  },

  // ── GameFindWordFromImageComponent ────────────────────────────────────────
  find_word_from_image: {
    loader: () => import('../../../components/games/templates/game-find-word-from-image/game-find-word-from-image.component').then(m => m.GameFindWordFromImageComponent),
  },

  // ── GameFindWordFromSoundComponent ────────────────────────────────────────
  find_word_from_sound: {
    loader: () => import('../../../components/games/templates/game-find-word-from-sound/game-find-word-from-sound.component').then(m => m.GameFindWordFromSoundComponent),
  },

  // ── GameFindSoundFromWordComponent ────────────────────────────────────────
  find_sound_from_word: {
    loader: () => import('../../../components/games/templates/game-find-sound-from-word/game-find-sound-from-word.component').then(m => m.GameFindSoundFromWordComponent),
  },
  zh_find_sound_from_pinyin: {
    loader: () => import('../../../components/games/templates/game-find-sound-from-word/game-find-sound-from-word.component').then(m => m.GameFindSoundFromWordComponent),
  },

  // ── GameFindSoundFromImageComponent ───────────────────────────────────────
  find_sound_from_image: {
    loader: () => import('../../../components/games/templates/game-find-sound-from-image/game-find-sound-from-image.component').then(m => m.GameFindSoundFromImageComponent),
  },

  // ── GameFindFromImageComponent ────────────────────────────────────────────
  find_from_image: {
    loader: () => import('../../../components/games/templates/game-find-from-image/game-find-from-image.component').then(m => m.GameFindFromImageComponent),
  },

  // ── GameFindTranslationFromSoundComponent ─────────────────────────────────
  find_translation_from_sound: {
    loader: () => import('../../../components/games/templates/game-find-translation-from-sound/game-find-translation-from-sound.component').then(m => m.GameFindTranslationFromSoundComponent),
  },

  // ── GameFindRandomComponent ───────────────────────────────────────────────
  find_random: {
    loader: () => import('../../../components/games/templates/game-find-random/game-find-random.component').then(m => m.GameFindRandomComponent),
  },

  // ── GameFindItemComponent ─────────────────────────────────────────────────
  find_item: {
    loader: () => import('../../../components/games/templates/game-find-item/game-find-item.component').then(m => m.GameFindItemComponent),
  },

  // ── GameChronoWordComponent ───────────────────────────────────────────────
  chrono_word: {
    loader: () => import('../../../components/games/templates/game-chrono-word/game-chrono-word.component').then(m => m.GameChronoWordComponent),
  },

  // ── GameChronoSentenceComponent ───────────────────────────────────────────
  chrono_sentence: {
    loader: () => import('../../../components/games/templates/game-chrono-sentence/game-chrono-sentence.component').then(m => m.GameChronoSentenceComponent),
  },

  // ── GameOperaComponent ────────────────────────────────────────────────────
  opera: {
    loader: () => import('../../../components/games/templates/game-opera/game-opera.component').then(m => m.GameOperaComponent),
    inputsFn: (game) => ({ isLevelGrapheme: game.level?.isGraphemeLevel }),
    submit: null,
    omitInputs: ['gameDesc', 'gameDescAudio'],
  },

  // ── GameFlashcardComponent ────────────────────────────────────────────────
  flash_card: {
    loader: () => import('../../../components/games/templates/game-flashcard/game-flashcard.component').then(m => m.GameFlashcardComponent),
    inputs: { displayStrokeOrder: false },
    submit: null,
  },
  zh_flash_card_stroke_order: {
    loader: () => import('../../../components/games/templates/game-flashcard/game-flashcard.component').then(m => m.GameFlashcardComponent),
    inputs: { displayStrokeOrder: true },
    submit: null,
  },
  ja_flash_card_kanji: {
    loader: () => import('../../../components/games/templates/game-flashcard/game-flashcard.component').then(m => m.GameFlashcardComponent),
    inputs: { displayStrokeOrder: false },
    submit: null,
  },

  // ── GameFlashcardGraphemeComponent ────────────────────────────────────────
  flash_card_grapheme: {
    loader: () => import('../../../components/games/templates/game-flashcard-grapheme/game-flashcard-grapheme.component').then(m => m.GameFlashcardGraphemeComponent),
    inputs: { displayStrokeOrder: false },
    submit: null,
  },
  ja_flash_card_grapheme_hiragana: {
    loader: () => import('../../../components/games/templates/game-flashcard-grapheme/game-flashcard-grapheme.component').then(m => m.GameFlashcardGraphemeComponent),
    inputs: { displayStrokeOrder: true },
    submit: null,
  },
  ja_flash_card_grapheme_katakana: {
    loader: () => import('../../../components/games/templates/game-flashcard-grapheme/game-flashcard-grapheme.component').then(m => m.GameFlashcardGraphemeComponent),
    inputs: { displayStrokeOrder: true },
    submit: null,
  },

  // ── GameEmersyaVocabularyComponent ────────────────────────────────────────
  '3d_vocabulary': {
    loader: () => import('../../../components/games/templates/game-emersya-vocabulary/game-emersya-vocabulary.component').then(m => m.GameEmersyaVocabularyComponent),
    submit: null,
  },

  // ── GameFindSentenceTranslationFromSoundComponent ─────────────────────────
  find_sentence_translation_from_sound: {
    loader: () => import('../../../components/games/templates/game-find-sentence-translation-from-sound/game-find-sentence-translation-from-sound.component').then(m => m.GameFindSentenceTranslationFromSoundComponent),
  },
  find_sentence_translation_from_sound_hard: {
    loader: () => import('../../../components/games/templates/game-find-sentence-translation-from-sound/game-find-sentence-translation-from-sound.component').then(m => m.GameFindSentenceTranslationFromSoundComponent),
  },

  // ── GameSentenceMistakeComponent ──────────────────────────────────────────
  sentence_mistake_easy: {
    loader: () => import('../../../components/games/templates/game-sentence-mistake/game-sentence-mistake.component').then(m => m.GameSentenceMistakeComponent),
  },
  sentence_mistake: {
    loader: () => import('../../../components/games/templates/game-sentence-mistake/game-sentence-mistake.component').then(m => m.GameSentenceMistakeComponent),
  },

  // ── GameSentenceOrderComponent ────────────────────────────────────────────
  sentence_order: {
    loader: () => import('../../../components/games/templates/game-sentence-order/game-sentence-order.component').then(m => m.GameSentenceOrderComponent),
  },

  // ── GameVerbConjugationComponent ──────────────────────────────────────────
  verb_conjugation: {
    loader: () => import('../../../components/games/templates/game-verb-conjugation/game-verb-conjugation.component').then(m => m.GameVerbConjugationComponent),
  },

  // ── GameConnectWordsComponent ─────────────────────────────────────────────
  connect_words: {
    loader: () => import('../../../components/games/templates/game-connect-words/game-connect-words.component').then(m => m.GameConnectWordsComponent),
  },

  // ── GameFindGenderComponent ───────────────────────────────────────────────
  find_gender: {
    loader: () => import('../../../components/games/templates/game-find-gender/game-find-gender.component').then(m => m.GameFindGenderComponent),
  },

  // ── GameWordDictationToSourceComponent ────────────────────────────────────
  word_dictation_to_source: {
    loader: () => import('../../../components/games/templates/game-word-dictation-to-source/game-word-dictation-to-source.component').then(m => m.GameWordDictationToSourceComponent),
  },

  // ── GameWordDictationComponent ────────────────────────────────────────────
  word_dictation: {
    loader: () => import('../../../components/games/templates/game-word-dictation/game-word-dictation.component').then(m => m.GameWordDictationComponent),
    submit: 'difficulty',
  },
  sentence_dictation: {
    loader: () => import('../../../components/games/templates/game-word-dictation/game-word-dictation.component').then(m => m.GameWordDictationComponent),
    submit: 'difficulty',
  },

  // ── GameGraphemeOrderComponent ────────────────────────────────────────────
  grapheme_order_audio: {
    loader: () => import('../../../components/games/templates/game-grapheme-order/game-grapheme-order.component').then(m => m.GameGraphemeOrderComponent),
    inputsFn: (game) => ({ gameTag: game.template }),
  },
  grapheme_order_text: {
    loader: () => import('../../../components/games/templates/game-grapheme-order/game-grapheme-order.component').then(m => m.GameGraphemeOrderComponent),
    inputsFn: (game) => ({ gameTag: game.template }),
  },

  // ── GameFindItemFromImageComponent ────────────────────────────────────────
  find_item_from_image: {
    loader: () => import('../../../components/games/templates/game-find-item-from-image/game-find-item-from-image.component').then(m => m.GameFindItemFromImageComponent),
  },

  // ── GameFindTimeFromWordComponent ─────────────────────────────────────────
  find_time_from_word: {
    loader: () => import('../../../components/games/templates/game-find-time-from-word/game-find-time-from-word.component').then(m => m.GameFindTimeFromWordComponent),
  },
  find_time_from_sentence: {
    loader: () => import('../../../components/games/templates/game-find-time-from-word/game-find-time-from-word.component').then(m => m.GameFindTimeFromWordComponent),
  },
  ja_find_time_or_form: {
    loader: () => import('../../../components/games/templates/game-find-time-from-word/game-find-time-from-word.component').then(m => m.GameFindTimeFromWordComponent),
  },

  // ── GameFindGraphemeFromPhonemeComponent ──────────────────────────────────
  find_grapheme_from_phoneme: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },
  ja_find_grapheme_hiragana_simple: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },
  ja_find_grapheme_katakana_simple: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },
  ja_find_grapheme_katakana_hiragana_simple: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },
  ja_find_grapheme_hiragana: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },
  ja_find_grapheme_katakana: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },
  find_grapheme_syllable_from_phoneme: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme/game-find-grapheme-from-phoneme.component').then(m => m.GameFindGraphemeFromPhonemeComponent),
  },

  // ── GameFindGraphemeFromPhonemeConfirmationComponent ──────────────────────
  ja_find_grapheme_katakana_hiragana: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-phoneme-confirmation/game-find-grapheme-from-phoneme-confirmation.component').then(m => m.GameFindGraphemeFromPhonemeConfirmationComponent),
  },

  // ── GameFindGraphemeFromWordComponent ─────────────────────────────────────
  find_grapheme_from_word: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-word/game-find-grapheme-from-word.component').then(m => m.GameFindGraphemeFromWordComponent),
  },

  // ── GameFindPhonemeFromGraphemeComponent ──────────────────────────────────
  find_phoneme_from_grapheme: {
    loader: () => import('../../../components/games/templates/game-find-phoneme-from-grapheme/game-find-phoneme-from-grapheme.component').then(m => m.GameFindPhonemeFromGraphemeComponent),
  },
  find_phoneme_from_grapheme_syllable: {
    loader: () => import('../../../components/games/templates/game-find-phoneme-from-grapheme/game-find-phoneme-from-grapheme.component').then(m => m.GameFindPhonemeFromGraphemeComponent),
  },

  // ── GameFindPronounFromVerbComponent ──────────────────────────────────────
  find_pronoun_from_verb: {
    loader: () => import('../../../components/games/templates/game-find-pronoun-from-verb/game-find-pronoun-from-verb.component').then(m => m.GameFindPronounFromVerbComponent),
  },

  // ── GameFindGrammaticalCaseComponent ──────────────────────────────────────
  find_grammatical_case: {
    loader: () => import('../../../components/games/templates/game-find-grammatical-case/game-find-grammatical-case.component').then(m => m.GameFindGrammaticalCaseComponent),
  },

  // ── GameWritingDeclensionComponent ────────────────────────────────────────
  writing_declension: {
    loader: () => import('../../../components/games/templates/game-writing-declension/game-writing-declension.component').then(m => m.GameWritingDeclensionComponent),
  },

  // ── GameGapTextComponent ──────────────────────────────────────────────────
  gap_text: {
    loader: () => import('../../../components/games/templates/game-gap-text/game-gap-text.component').then(m => m.GameGapTextComponent),
  },

  // ── GameSkillGapTextComponent ─────────────────────────────────────────────
  skill_gap_text: {
    loader: () => import('../../../components/games/templates/game-skill-gap-text/game-skill-gap-text.component').then(m => m.GameSkillGapTextComponent),
  },

  // ── GameDialogueComponent ─────────────────────────────────────────────────
  dialogue: {
    loader: () => import('../../../components/games/templates/game-dialogue/game-dialogue.component').then(m => m.GameDialogueComponent),
  },

  // ── GameAudioOrderComponent ───────────────────────────────────────────────
  audio_order: {
    loader: () => import('../../../components/games/templates/game-audio-order/game-audio-order.component').then(m => m.GameAudioOrderComponent),
  },
  audio_order_difficult: {
    loader: () => import('../../../components/games/templates/game-audio-order/game-audio-order.component').then(m => m.GameAudioOrderComponent),
    inputs: { gameAudioDifficult: true },
  },

  // ── GameSudokuComponent ───────────────────────────────────────────────────
  sudoku: {
    loader: () => import('../../../components/games/templates/game-sudoku/game-sudoku.component').then(m => m.GameSudokuComponent),
    submit: 'difficulty',
  },

  // ── GameCalculsComponent ──────────────────────────────────────────────────
  calculs: {
    loader: () => import('../../../components/games/templates/game-calculs/game-calculs.component').then(m => m.GameCalculsComponent),
    submit: 'difficulty',
  },

  // ── GameFromToComponent ───────────────────────────────────────────────────
  ja_find_kanji_from_kana: {
    loader: () => import('../../../components/games/templates/game-from-to/game-from-to.component').then(m => m.GameFromToComponent),
  },
  ja_find_kana_from_kanji: {
    loader: () => import('../../../components/games/templates/game-from-to/game-from-to.component').then(m => m.GameFromToComponent),
  },
  ja_find_kanji_from_sound: {
    loader: () => import('../../../components/games/templates/game-from-to/game-from-to.component').then(m => m.GameFromToComponent),
  },
  zh_find_pinyin_from_sound: {
    loader: () => import('../../../components/games/templates/game-from-to/game-from-to.component').then(m => m.GameFromToComponent),
  },
  zh_find_pinyin_from_hanzi: {
    loader: () => import('../../../components/games/templates/game-from-to/game-from-to.component').then(m => m.GameFromToComponent),
  },
  find_word_from_definition: {
    loader: () => import('../../../components/games/templates/game-from-to/game-from-to.component').then(m => m.GameFromToComponent),
  },

  // ── GameWriteTranslationFromWordComponent ─────────────────────────────────
  write_translation_from_word: {
    loader: () => import('../../../components/games/templates/game-write-translation-from-word/game-write-translation-from-word.component').then(m => m.GameWriteTranslationFromWordComponent),
  },

  // ── GameFindConjugationComponent ──────────────────────────────────────────
  ja_find_conjugation: {
    loader: () => import('../../../components/games/templates/game-find-conjugation/game-find-conjugation.component').then(m => m.GameFindConjugationComponent),
  },

  // ── GameFindIrregularVerbComponent ────────────────────────────────────────
  en_find_irregular_verb: {
    loader: () => import('../../../components/games/templates/game-find-irregular-verb/game-find-irregular-verb.component').then(m => m.GameFindIrregularVerbComponent),
  },

  // ── GameFindKeyComponent ──────────────────────────────────────────────────
  zh_find_key_from_word: {
    loader: () => import('../../../components/games/templates/game-find-key/game-find-key.component').then(m => m.GameFindKeyComponent),
  },

  // ── GameFindAccentComponent ───────────────────────────────────────────────
  zh_find_accent_from_sound: {
    loader: () => import('../../../components/games/templates/game-find-accent/game-find-accent.component').then(m => m.GameFindAccentComponent),
  },

  // ── GameFindPositionFromGraphemeComponent ─────────────────────────────────
  ar_find_position_from_grapheme: {
    loader: () => import('../../../components/games/templates/game-find-position-from-grapheme/game-find-position-from-grapheme.component').then(m => m.GameFindPositionFromGraphemeComponent),
  },

  // ── GameFindGraphemeFromPositionComponent ─────────────────────────────────
  ar_find_grapheme_from_position: {
    loader: () => import('../../../components/games/templates/game-find-grapheme-from-position/game-find-grapheme-from-position.component').then(m => m.GameFindGraphemeFromPositionComponent),
  },

  // ── GameWriteFromToComponent ──────────────────────────────────────────────
  zh_write_pinyin_from_word: {
    loader: () => import('../../../components/games/templates/game-write-from-to/game-write-from-to.component').then(m => m.GameWriteFromToComponent),
    submit: 'difficulty',
  },
  zh_write_pinyin_from_sound: {
    loader: () => import('../../../components/games/templates/game-write-from-to/game-write-from-to.component').then(m => m.GameWriteFromToComponent),
    submit: 'difficulty',
  },

  // ── GameDrawFromToComponent ───────────────────────────────────────────────
  zh_draw_from_word: {
    loader: () => import('../../../components/games/templates/game-draw-from-to/game-draw-from-to.component').then(m => m.GameDrawFromToComponent),
    inputs: { showOutline: true },
  },
  zh_draw_from_sound: {
    loader: () => import('../../../components/games/templates/game-draw-from-to/game-draw-from-to.component').then(m => m.GameDrawFromToComponent),
    inputs: { showOutline: false },
  },

  // ── GameDrawFromToHiraganaComponent ───────────────────────────────────────
  ja_draw_hiragana: {
    loader: () => import('../../../components/games/templates/game-draw-from-to-hiragana/game-draw-from-to-hiragana.component').then(m => m.GameDrawFromToHiraganaComponent),
    inputs: { showOutline: true },
  },
  ja_draw_katakana: {
    loader: () => import('../../../components/games/templates/game-draw-from-to-hiragana/game-draw-from-to-hiragana.component').then(m => m.GameDrawFromToHiraganaComponent),
    inputs: { showOutline: true },
  },
  ja_draw_katakana_from_sound: {
    loader: () => import('../../../components/games/templates/game-draw-from-to-hiragana/game-draw-from-to-hiragana.component').then(m => m.GameDrawFromToHiraganaComponent),
    inputs: { showOutline: false },
  },
  ja_draw_hiragana_from_sound: {
    loader: () => import('../../../components/games/templates/game-draw-from-to-hiragana/game-draw-from-to-hiragana.component').then(m => m.GameDrawFromToHiraganaComponent),
    inputs: { showOutline: false },
  },
};
